import { useCallback, useEffect, useRef, useState } from "react";
import { GetWalletAlerts } from "../../Service/_index";

// Fired on `window` when money or a minimum changed. The bell does not call the server for it; it forgets what it
// knew, so the next click on the bell looks again.
export const WALLET_ALERTS_REFRESH_EVENT = "wallet-alerts:refresh";

const typesOf = (userType) =>
  String(userType || "")
    .split(",")
    .map((type) => type.trim().toLowerCase());

/** Only the admin (CLIENT) login and a "user" login have wallet alerts; the stored type can be a comma-joined list. */
export const hasWalletAlerts = (userType) => typesOf(userType).some((type) => type === "client" || type === "user");
export const isUserLogin = (userType) => typesOf(userType).includes("user");

/** Tell the bell its last answer is out of date. It does not call the server; the next click on the bell does. */
export const requestWalletAlertsRefresh = () => window.dispatchEvent(new Event(WALLET_ALERTS_REFRESH_EVENT));

/**
 * The navbar bell's data: the low-balance alerts the server works out from the signed-in login's own wallets.
 *
 * The server is asked only when the person clicks the bell (`refresh`), never on page load, on a timer, or when the
 * tab is focused, and never for a login without wallet alerts. A failed look keeps the alerts already known (and sets
 * `error`) rather than blanking them. An older answer never overwrites a newer one.
 *
 * When a wallet page reports that money or a minimum changed (WALLET_ALERTS_REFRESH_EVENT), what the bell knew is
 * dropped, so a stale count is never shown; the next click looks again.
 */
const useWalletAlerts = (userType) => {
  const enabled = hasWalletAlerts(userType);
  // A different login in the same page (sign out / sign in) must never see the previous one's alerts.
  const identity = `${userType}|${sessionStorage.getItem("clientID")}|${sessionStorage.getItem("UserId")}`;

  const [alerts, setAlerts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const latest = useRef(0);

  const look = useCallback(() => {
    if (!enabled) return Promise.resolve();
    const id = ++latest.current;
    setLoading(true);
    return GetWalletAlerts().then((res) => {
      if (id !== latest.current) return; // superseded (or the bell is gone)
      setLoading(false);
      if (res.error) {
        setError(res.message || "Could not load the alerts");
        return;
      }
      setAlerts(Array.isArray(res.data?.alerts) ? res.data.alerts : []);
      setLoaded(true);
      setError("");
    });
  }, [enabled]);

  useEffect(() => {
    const forget = () => {
      latest.current += 1; // an answer still on its way is ignored
      setAlerts([]);
      setLoaded(false);
      setLoading(false);
      setError("");
    };
    forget();
    if (!enabled) return undefined;

    window.addEventListener(WALLET_ALERTS_REFRESH_EVENT, forget);
    return () => {
      window.removeEventListener(WALLET_ALERTS_REFRESH_EVENT, forget);
      latest.current += 1;
    };
  }, [enabled, identity]);

  return { enabled, alerts, count: alerts.length, loaded, loading, error, refresh: look };
};

export default useWalletAlerts;
